import React, { FunctionComponent, MutableRefObject } from 'react';
import Image from 'next/image';
import Title from '../Title';
import PostSidebar from '../PostSidebar';
import PostShare from '../PostShare';
import MetaItem from '../MetaItem';
import MarkdownContent from '../MarkdownContent';
import styles from '../Post/Post.module.scss';
import contentStyles from '../Content/Content.module.scss';
import coverStyles from '../PostCover/PostCover.module.scss';
import summaryStyles from '../PostSummary/PostSummary.module.scss';
import sourceStyles from '../PostSources/PostSources.module.scss';
import { useTranslations } from '../../hooks/useTranslations';
import type { MarkdownArticle } from '../../lib/articles';
import type { Author } from '../../types/common/Author.types';

interface MarkdownPostProps {
  article: MarkdownArticle;
  author: Author;
  numberOfComments: number;
  commentsBlockRef: MutableRefObject<HTMLDivElement>;
  createdDate?: string;
}

const splitSourcesSection = (content: string) => {
  const sourceHeadingMatch = content.match(/\n##\s+(Źródła|Sources)\s*\n/i);

  if (!sourceHeadingMatch) {
    return {
      content,
      sources: [],
    };
  }

  const sourceStartIndex = sourceHeadingMatch.index;
  const sourcesContent = content.slice(sourceStartIndex + sourceHeadingMatch[0].length);

  return {
    content: content.slice(0, sourceStartIndex).trim(),
    sources: sourcesContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('- '))
      .map((line) => line.replace(/^-\s+/, '')),
  };
};

const renderSource = (source: string) => {
  const markdownLink = source.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  const malformedMarkdownLink = source.match(/^\(([^)]+)\)\[([^\]]+)\]$/);
  const match = markdownLink || malformedMarkdownLink;

  if (match) {
    return (
      <a href={match[2]} target="_blank" rel="noopener noreferrer nofollow">
        {match[1]}
      </a>
    );
  }

  const parts = source.split(/(_[^_]+_)/g);

  return parts.map((part, index) => {
    const emphasis = part.match(/^_([^_]+)_$/);

    return emphasis
      ? <em key={`${source}-em-${index}`}>{emphasis[1]}</em>
      : part;
  });
};

const MarkdownPost: FunctionComponent<MarkdownPostProps> = ({
  article,
  author,
  numberOfComments,
  commentsBlockRef,
  createdDate,
}) => {
  const { t } = useTranslations();
  const { content, sources } = splitSourcesSection(article.content);

  return (
    <article className={styles.wrapper}>
      <Title classes={styles.title}>{article.title}</Title>
      {article.excerpt && (
        <div className={styles.excerpt}>
          <p>{article.excerpt}</p>
        </div>
      )}
      <div className={styles.metabar}>
        {article.categoryName && <MetaItem title={t.ARTICLE.META.CATEGORY} value={article.categoryName} />}
        {createdDate && <MetaItem title={t.ARTICLE.META.PUBLICATION_DATE} value={createdDate} />}
        {article.level && <MetaItem title={t.ARTICLE.META.LEVEL} value={article.level} />}
      </div>
      {article.featuredImage && (
        <figure className={coverStyles.wrapper}>
          <Image
            src={article.featuredImage.src}
            width={1147}
            height={460}
            alt={article.featuredImage.alt || article.featuredImage.title || article.title}
            className={coverStyles.image}
          />
        </figure>
      )}
      <div className={styles.content}>
        <PostSidebar author={author} numberOfComments={numberOfComments} commentsBlockRef={commentsBlockRef}/>
        <div className={contentStyles.content}>
          {article.summary?.length > 0 && (
            <section className={summaryStyles.summary}>
              <h2 className={summaryStyles.title}>{t.ARTICLE.SUMMARY.TITLE}</h2>
              <ul>
                {article.summary.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>
          )}
          <MarkdownContent
            content={content}
            highlightedCodeBlocks={article.highlightedCodeBlocks}
            locale={article.locale}
            related={article.related}
          />
          <div className={contentStyles.dots}></div>
          {sources.length > 0 && (
            <section className={sourceStyles.sources}>
              <h3 className={sourceStyles.title}>{t.ARTICLE.SOURCES}</h3>
              <ul className={sourceStyles.markdownSourcesList}>
                {sources.map((source) => (
                  <li className={sourceStyles.markdownSourceItem} key={source}>{renderSource(source)}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
      <PostShare />
    </article>
  );
};

export default MarkdownPost;
